import { FC, memo, useCallback, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Seo } from 'components'
import { SuspenseData } from 'components/extend/SuspenseData'
import { OrderCopy } from 'schema'

import { useGetCommonOperationsQuery, useGetOrderCopyQuery, useGetPointOperationsQuery } from '../../api'
import { orderCopyRoute, orderRoute } from '../../routes'
import { TOrderCreateFormData } from '../../types/orderCreateTypes'
import { filterAvailableOperations, transform } from './helpers'
import { OrderCopyForm } from './OrderCopyForm'

type AvailableOperations = {
  common: Set<string>
  departure: Set<string>
  destination: Set<string>
}

type OrderCopyWrapperProps = {
  available: AvailableOperations
  data: OrderCopy
}

const OrderCopyWrapper: FC<OrderCopyWrapperProps> = ({ data, available }) => {
  const [initial] = useState<TOrderCreateFormData>(() => {
    const transformed = transform(data)

    return {
      ...transformed,
      operation: filterAvailableOperations(
        transformed.operation ?? [],
        available,
        data.departure.point?.terminal?.id,
        data.destination.point?.terminal?.id
      )
    }
  })

  return <OrderCopyForm initial={initial} />
}

export const OrderCopyRoute: FC = () => {
  const { id } = orderCopyRoute.useParams()
  const { data, isLoading: isLoadingData, isError, isSuccess } = useGetOrderCopyQuery(id)
  const navigate = useNavigate()
  const { data: commonOperations = [], isSuccess: isCommonSuccess } = useGetCommonOperationsQuery()
  const { data: departureOperations = [], isSuccess: isDepartureSuccess } = useGetPointOperationsQuery({
    type: 'departure'
  })
  const { data: destinationOperations = [], isSuccess: isDestinationSuccess } = useGetPointOperationsQuery({
    type: 'destination'
  })

  const handleClose = useCallback(() => {
    navigate(orderRoute)
  }, [navigate])

  const available = useMemo<AvailableOperations>(
    () => ({
      common: new Set(commonOperations.map((op) => op.id)),
      departure: new Set(departureOperations.map((op) => op.id)),
      destination: new Set(destinationOperations.map((op) => op.id))
    }),
    [commonOperations, departureOperations, destinationOperations]
  )

  const isReady = isSuccess && isCommonSuccess && isDepartureSuccess && isDestinationSuccess

  return (
    <>
      <Seo
        description="Создание новой заявки для размещения в сервисе доставки сборных грузов QP5 Express. Заполнение данных о грузе и предварительный расчет стоимости грузоперевозки."
        title="Новая заявка – сборные перевозки QP5 Express"
      />
      <SuspenseData<OrderCopy>
        data={data}
        isError={isError}
        isLoading={isLoadingData || !isReady}
        isSuccess={isSuccess}
        onClose={handleClose}
      >
        {(orderData) => <OrderCopyWrapper available={available} data={orderData} />}
      </SuspenseData>
    </>
  )
}

export default memo(OrderCopyRoute)
